import { MapPin, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { FadeIn, TextReveal, useCountUp, GlowCard, BlurReveal } from "../components/Animations";
import { useRef } from "react";

// 12 Counties as per the deck
const counties = [
  "Kakamega",
  "Trans Nzoia",
  "Uasin Gishu",
  "Kisumu",
  "Kericho",
  "West Pokot",
  "Nakuru",
  "Kiambu",
  "Nairobi",
  "Mombasa",
  "Kilifi",
  "Garissa"
];

const commitments = [
  { letter: "A", title: "Strengthen Care Systems", desc: "Strengthen maternal and newborn care systems across 12 high-burden counties, reaching nearly half a million births every year." },
  { letter: "B", title: "Scale Innovations", desc: "Introduce, integrate and scale at least 20 scale-ready innovations across 12 counties." },
  { letter: "C", title: "Align Financing", desc: "Work with governments, philanthropic leaders, and partners to align US$75 million in public, philanthropic and catalytic capital." },
  { letter: "D", title: "Establish Learning Labs", desc: "Establish Healthy Birth Learning Labs to test solutions, generate implementation evidence, and share lessons nationally and globally." },
];

const outcomeDomains = [
  "Reduction in Case Fatality Among Small and Sick Newborns",
  "Improvement in Intrapartum Quality of Care",
  "Reduction in Referral Delays and Care Fragmentation",
  "Institutionalization of Data-Driven Governance",
  "Reduction of Low Birth Weight Incidence",
];

// Animated Kenyan Mother & Child Icon
function AnimatedMotherChild({ className = "", color = "currentColor" }) {
  return (
    <motion.svg
      viewBox="0 0 100 100"
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
    >
      <motion.path
        d="M50 15 C42 15 36 22 36 32 C36 42 42 48 48 52 C45 56 42 65 42 78 C42 88 48 95 50 95 C52 95 58 88 58 78 C58 65 55 56 52 52 C58 48 64 42 64 32 C64 22 58 15 50 15"
        fill="none"
        stroke={color}
        strokeWidth="2.5"
        strokeLinecap="round"
        variants={{
          hidden: { pathLength: 0, opacity: 0 },
          visible: { pathLength: 1, opacity: 1, transition: { duration: 2, ease: "easeInOut" } }
        }}
      />
      <motion.ellipse
        cx="50" cy="65" rx="8" ry="10"
        fill="none"
        stroke={color}
        strokeWidth="2"
        variants={{
          hidden: { pathLength: 0, opacity: 0 },
          visible: { pathLength: 1, opacity: 1, transition: { duration: 1.5, delay: 1.5, ease: "easeInOut" } }
        }}
      />
      <motion.path
        d="M48 62 C48 59 50 58 50 60 C50 58 52 59 52 62 L50 66 Z"
        fill={color}
        variants={{
          hidden: { scale: 0, opacity: 0 },
          visible: { scale: 1, opacity: 1, transition: { duration: 0.5, delay: 2.5 } }
        }}
      />
    </motion.svg>
  );
}

// Animated African Pattern
function AnimatedAfricanPattern({ className = "" }) {
  return (
    <motion.svg
      viewBox="0 0 200 40"
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
    >
      {[0, 1, 2, 3, 4].map((i) => (
        <motion.path
          key={i}
          d={`M${40 * i + 20} 5 L${40 * i + 35} 20 L${40 * i + 20} 35 L${40 * i + 5} 20 Z`}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          variants={{
            hidden: { pathLength: 0, opacity: 0 },
            visible: { 
              pathLength: 1, 
              opacity: 1,
              transition: { duration: 0.8, delay: i * 0.15, ease: "easeInOut" }
            }
          }}
        />
      ))}
    </motion.svg>
  );
}

// Animated Heartbeat Line
function AnimatedHeartbeat({ className = "" }) {
  return (
    <motion.svg
      viewBox="0 0 200 50"
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
    >
      <motion.path
        d="M0 25 L30 25 L40 25 L50 10 L60 40 L70 15 L80 35 L90 25 L200 25"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        variants={{
          hidden: { pathLength: 0 },
          visible: { pathLength: 1, transition: { duration: 2, ease: "easeInOut" } }
        }}
      />
    </motion.svg>
  );
}

// Animated Maasai Shield
function AnimatedMaasaiShield({ className = "" }) {
  return (
    <motion.svg
      viewBox="0 0 80 100"
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
    >
      <motion.path
        d="M40 5 C20 5 10 20 10 40 C10 70 40 95 40 95 C40 95 70 70 70 40 C70 20 60 5 40 5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        variants={{
          hidden: { pathLength: 0, opacity: 0 },
          visible: { pathLength: 1, opacity: 1, transition: { duration: 1.5, ease: "easeInOut" } }
        }}
      />
      <motion.line x1="40" y1="15" x2="40" y2="85" stroke="currentColor" strokeWidth="2"
        variants={{ hidden: { pathLength: 0 }, visible: { pathLength: 1, transition: { duration: 0.8, delay: 1.2 } } }}
      />
      <motion.path d="M20 30 L60 30" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
        variants={{ hidden: { pathLength: 0 }, visible: { pathLength: 1, transition: { duration: 0.5, delay: 1.5 } } }}
      />
      <motion.path d="M15 50 L65 50" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
        variants={{ hidden: { pathLength: 0 }, visible: { pathLength: 1, transition: { duration: 0.5, delay: 1.7 } } }}
      />
      <motion.path d="M22 70 L58 70" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
        variants={{ hidden: { pathLength: 0 }, visible: { pathLength: 1, transition: { duration: 0.5, delay: 1.9 } } }}
      />
    </motion.svg>
  );
}

export default function ImpactPage() {
  const { count: countyCount, ref: countyRef } = useCountUp(12, 1500);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 150]);

  return (
    <div data-testid="impact-page" className="pt-16 overflow-hidden">
      {/* Hero */}
      <section ref={heroRef} className="bg-navy-900 min-h-[90vh] flex items-center relative overflow-hidden">
        <motion.div className="absolute inset-0 flex items-center justify-center opacity-5">
          <AnimatedAfricanPattern className="w-full max-w-4xl text-white" />
        </motion.div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div style={{ y: heroY }}>
              <FadeIn>
                <div className="flex items-center gap-4 mb-8">
                  <AnimatedMotherChild className="w-14 h-14 text-ochre-light" />
                  <p className="text-ochre-light font-body text-sm tracking-[0.3em] uppercase">The Kenya Commitment</p>
                </div>
              </FadeIn>
              
              <TextReveal delay={0.2}>
                <h1 data-testid="impact-title" className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1] mb-8 tracking-tight">
                  Our Commitment in <span className="text-ochre-light">Kenya</span>
                </h1>
              </TextReveal>
              
              <FadeIn delay={0.5}>
                <p className="text-white/50 font-body text-lg md:text-xl leading-relaxed max-w-xl mb-10">
                  MoH Kenya and Jhpiego are coming together to announce the Healthy Birth Initiative with an aspirational goal of 30% reduction in low birthweight and 50% reduction in neonatal deaths over the next 5 years.
                </p>
              </FadeIn>
              
              <FadeIn delay={0.7}>
                <Link 
                  to="/contact" 
                  className="inline-flex items-center gap-2 bg-ochre text-white px-6 py-3 font-body font-medium rounded-full hover:bg-ochre/90 transition-all"
                >
                  Join the Initiative <ArrowRight size={18} />
                </Link>
              </FadeIn>
            </motion.div>
            
            <div className="relative h-[400px] hidden lg:block">
              <motion.div 
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                animate={{ y: [0, -5, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                <AnimatedMotherChild className="w-32 h-32 text-ochre-light" />
              </motion.div>
              <motion.div className="absolute top-10 left-1/2 -translate-x-1/2 opacity-60">
                <AnimatedHeartbeat className="w-40 h-12 text-white" />
              </motion.div>
            </div>
          </div>
        </div>
        
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-ochre to-transparent opacity-50" />
      </section>

      {/* Goals */}
      <section className="bg-white py-32 relative overflow-hidden">
        <motion.div className="absolute top-10 right-10 opacity-5">
          <AnimatedAfricanPattern className="w-48 text-navy" />
        </motion.div>
        
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
              <FadeIn>
                <GlowCard glowColor="rgba(15, 39, 68, 0.3)">
                  <motion.div 
                    className="bg-navy p-10 md:p-12 rounded-2xl h-full relative overflow-hidden" 
                    data-testid="goal-card-0"
                    whileHover={{ scale: 1.02 }}
                  >
                    <motion.div className="absolute top-4 right-4 opacity-20">
                      <AnimatedHeartbeat className="w-20 h-8 text-white" />
                    </motion.div>
                    <p className="text-white/30 font-body text-xs tracking-[0.3em] uppercase mb-4">The First Promise</p>
                    <motion.div 
                      className="text-6xl md:text-7xl font-heading font-bold text-white mb-4 tracking-tight"
                      initial={{ opacity: 0, scale: 0.5 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                    >
                      50%
                    </motion.div>
                    <p className="text-white/50 font-body text-base leading-relaxed">reduction in neonatal deaths</p>
                  </motion.div>
                </GlowCard>
              </FadeIn>
              <FadeIn delay={0.15}>
                <GlowCard glowColor="rgba(221, 107, 32, 0.3)">
                  <motion.div 
                    className="bg-ochre p-10 md:p-12 rounded-2xl h-full relative overflow-hidden" 
                    data-testid="goal-card-1"
                    whileHover={{ scale: 1.02 }}
                  >
                    <p className="text-white/60 font-body text-xs tracking-[0.3em] uppercase mb-4">The Second Promise</p>
                    <motion.div 
                      className="text-6xl md:text-7xl font-heading font-bold text-white mb-4 tracking-tight"
                      initial={{ opacity: 0, scale: 0.5 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.2 }}
                    >
                      30%
                    </motion.div>
                    <p className="text-white/80 font-body text-base leading-relaxed">reduction in low birth weight</p>
                  </motion.div>
                </GlowCard>
              </FadeIn>
            </div>
            
            <div className="hidden lg:flex justify-center">
              <motion.div
                animate={{ rotate: [0, 5, -5, 0] }}
                transition={{ duration: 8, repeat: Infinity }}
              >
                <AnimatedMotherChild className="w-40 h-40 text-navy/20" />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Will Deliver */}
      <section className="bg-[#fafafa] py-32">
        <div className="max-w-6xl mx-auto px-6">
          <FadeIn>
            <p className="text-ochre font-body text-xs tracking-[0.3em] uppercase mb-6">Our Commitments</p>
          </FadeIn>
          <FadeIn delay={0.15}>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-navy mb-16 tracking-tight">What We Will Deliver</h2>
          </FadeIn>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {commitments.map((item, i) => (
              <FadeIn key={item.letter} delay={i * 0.1}>
                <motion.div 
                  className="bg-white p-8 rounded-2xl group hover:bg-navy transition-all duration-500"
                  whileHover={{ y: -5 }}
                >
                  <div className="w-12 h-12 bg-ochre/10 group-hover:bg-ochre/20 flex items-center justify-center rounded-full mb-4 transition-colors duration-500">
                    <span className="text-ochre group-hover:text-ochre-light font-heading font-bold text-lg transition-colors duration-500">{item.letter}</span>
                  </div>
                  <h3 className="font-heading text-xl font-bold text-navy group-hover:text-white mb-3 tracking-tight transition-colors duration-500">{item.title}</h3>
                  <p className="text-slate-500 group-hover:text-white/60 font-body text-sm leading-relaxed transition-colors duration-500">{item.desc}</p>
                </motion.div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Counties */}
      <section className="bg-white py-32 relative overflow-hidden">
        <motion.div 
          className="absolute left-10 top-20 opacity-5"
          animate={{ y: [0, -15, 0], rotate: [0, 5, 0] }}
          transition={{ duration: 8, repeat: Infinity }}
        >
          <AnimatedMaasaiShield className="w-32 h-40 text-navy" />
        </motion.div>
        
        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="flex items-center gap-4 mb-6">
            <AnimatedMaasaiShield className="w-10 h-12 text-ochre" />
            <FadeIn><p className="text-ochre font-body text-xs font-medium tracking-[0.3em] uppercase">Our Focused Geographies</p></FadeIn>
          </div>
          
          <FadeIn delay={0.15}>
            <h2 className="font-heading text-4xl md:text-6xl font-semibold text-navy mb-6 tracking-tight">
              <span ref={countyRef} className="text-ochre">{countyCount}</span> Counties Across Kenya
            </h2>
          </FadeIn>
          <FadeIn delay={0.3}>
            <p className="text-slate-500 font-body text-lg mb-16 max-w-2xl leading-relaxed">
              12 counties that account for over half of Kenya's neonatal mortality burden and nearly half of all low-birthweight births. Nearly half a million births every year.
            </p>
          </FadeIn>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {counties.map((county, i) => (
              <FadeIn key={county} delay={i * 0.05}>
                <motion.div 
                  className="bg-[#fafafa] p-6 rounded-xl text-center hover:bg-navy hover:text-white transition-all duration-300 group cursor-default"
                  whileHover={{ y: -5, scale: 1.02 }}
                  data-testid={`county-${i}`}
                >
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <MapPin size={14} className="text-ochre group-hover:text-ochre-light transition-colors" />
                  </div>
                  <p className="font-body text-sm font-medium text-navy group-hover:text-white transition-colors">{county}</p>
                </motion.div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Outcome Domains */}
      <section className="bg-navy-900 py-32 relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-6 relative">
          <FadeIn><p className="text-ochre-light font-body text-xs tracking-[0.3em] uppercase mb-6">What Success Looks Like</p></FadeIn>
          <FadeIn delay={0.15}>
            <h2 className="font-heading text-4xl md:text-6xl font-bold text-white mb-16 tracking-tight">Five Outcome Domains</h2>
          </FadeIn>
          
          <div className="space-y-4">
            {outcomeDomains.map((domain, i) => (
              <FadeIn key={domain} delay={i * 0.1}>
                <motion.div 
                  className="flex items-center gap-6 bg-white/5 backdrop-blur-sm p-6 md:p-8 hover:bg-white/10 transition-all duration-500 rounded-2xl border border-white/5" 
                  data-testid={`outcome-domain-${i}`}
                  whileHover={{ x: 10, scale: 1.01 }}
                >
                  <motion.div 
                    className="w-12 h-12 bg-gradient-to-br from-ochre to-ochre-light flex items-center justify-center shrink-0 rounded-2xl"
                    whileHover={{ rotate: 10 }}
                  >
                    <span className="text-white font-heading font-bold text-lg">{i + 1}</span>
                  </motion.div>
                  <p className="text-white font-body text-base md:text-lg">{domain}</p>
                </motion.div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#faf9f7] py-32 relative overflow-hidden">
        <motion.div 
          className="absolute right-10 bottom-10 opacity-10"
          animate={{ y: [0, -15, 0] }}
          transition={{ duration: 5, repeat: Infinity }}
        >
          <AnimatedMotherChild className="w-32 h-32 text-navy" />
        </motion.div>
        
        <div className="max-w-4xl mx-auto px-6 text-center relative">
          <BlurReveal>
            <AnimatedAfricanPattern className="w-40 h-10 mx-auto mb-8 text-ochre" />
          </BlurReveal>
          <FadeIn delay={0.2}>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-navy mb-6 tracking-tight">
              Kenya is the first chapter.<br />The story is global.
            </h2>
          </FadeIn>
          <FadeIn delay={0.4}>
            <p className="text-slate-500 font-body text-lg mb-12 max-w-2xl mx-auto leading-relaxed">
              Lessons from Kenya will inform how governments across South Asia, West Africa, and beyond can accelerate their own progress.
            </p>
          </FadeIn>
          <FadeIn delay={0.6}>
            <Link 
              to="/contact" 
              data-testid="impact-cta" 
              className="inline-flex items-center gap-2 bg-navy text-white px-10 py-5 text-lg font-body font-medium rounded-full transition-all duration-300 hover:bg-navy-800 hover:shadow-2xl hover:scale-105"
            >
              Support the Initiative <ArrowRight size={20} />
            </Link>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
